import Vue from 'vue'
import VueRouter from 'vue-router'
import Login from '@/components/Login/Login'
import PublicPagamentoAvaliacao from '@/components/Public/PublicPagamentoAvaliacao'
import App from '@/App'
import ImoveisRoot from '@/components/Imoveis/ImoveisRoot'
import ImoveisLista from '@/components/Imoveis/ImoveisLista'
import ImoveisMapa from '@/components/Imoveis/ImoveisMapa'
import ImoveisAdicionar from '@/components/Imoveis/ImoveisAdicionar'
import ImoveisDetalhes from '@/components/Imoveis/ImoveisDetalhes'
import ImoveisDetalhesResumo from '@/components/Imoveis/Detalhes/DetalhesResumo'
import ImoveisDetalhesInfo from '@/components/Imoveis/Detalhes/DetalhesInfo'
import ImoveisDetalhesObservacoes from '@/components/Imoveis/Detalhes/DetalhesObservacoes'
import ImoveisDetalhesAnexos from '@/components/Imoveis/Detalhes/DetalhesAnexos'
import ImoveisDetalhesFotos from '@/components/Imoveis/Detalhes/DetalhesFotos'
import ImoveisDetalhesEndereco from '@/components/Imoveis/Detalhes/DetalhesEndereco'
import ImoveisDetalhesRegistro from '@/components/Imoveis/Detalhes/DetalhesRegistro'
import ImoveisDetalhesArea from '@/components/Imoveis/Detalhes/DetalhesArea'
import ImoveisDetalhesTaxas from '@/components/Imoveis/Detalhes/DetalhesTaxas'
import ImoveisDetalhesValor from '@/components/Imoveis/Detalhes/DetalhesValor'
import ImoveisDetalhesStatus from '@/components/Imoveis/Detalhes/DetalhesStatus'
import ImoveisDetalhesPagamentos from '@/components/Imoveis/Pagamentos/PagamentosMain'
import VistoriaAdicionar from '@/components/Vistoria/VistoriaAdicionar'
import VistoriaEditar from '@/components/Vistoria/VistoriaEditar'
import TarefasLista from '@/components/Tarefas/TarefasLista'
import TarefasAdicionar from '@/components/Tarefas/TarefasAdicionar'
import TarefasEditar from '@/components/Tarefas/TarefasEditar'
import RelatoriosRoot from '@/components/Relatorios/RelatoriosRoot'
import RelatoriosHistorico from '@/components/Relatorios/RelatoriosHistorico'
import RelatoriosComparativos from '@/components/Relatorios/RelatoriosComparativos'
import RelatoriosPagamentos from '@/components/Relatorios/RelatoriosPagamentos'
import Usuarios from '@/components/Usuarios/UsuariosMain'
import UsuariosLista from '@/components/Usuarios/UsuariosLista'
import UsuariosAdicionar from '@/components/Usuarios/UsuariosAdicionar'
import UsuariosEditar from '@/components/Usuarios/UsuariosEditar'
import UsuariosConfiguracoes from '@/components/Usuarios/UsuariosConfiguracoes'
import RolesLista from '@/components/Roles/RolesLista'
import RolesAdicionar from '@/components/Roles/RolesAdicionar'
import RolesEditar from '@/components/Roles/RolesEditar'
import Logout from '@/components/Login/Logout'
import Forbidden from '@/components/Utils/Forbidden'
import {
  hasCustomPermissionsPayload,
  shouldUseCustomPermissions,
  isCustomPermissionsPayloadInvalid,
  hasGeneralAccess as hasGeneralAccessFromUser,
  hasGranularPermission as hasGranularPermissionFromUser,
  normalizePermissions,
  hasGeneralAccessFromPermissions
} from '@/utils/access'

Vue.use(VueRouter)

const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth < 960

const getCurrentUser = () => {
  if (!Vue.auth || typeof Vue.auth.user !== 'function') return null
  return Vue.auth.user()
}

const hasGeneralAccess = (permissionKey) => {
  const user = getCurrentUser()
  if (!user) return false

  if (shouldUseCustomPermissions(user)) {
    return hasGeneralAccessFromUser(user, permissionKey)
  }

  if (hasCustomPermissionsPayload(user)) return false

  if (permissionKey === 'administracao_usuarios') {
    return Vue.auth.check(['Administrador', 'Patrimônio'])
  }

  if (permissionKey === 'administracao_niveis_acesso') {
    return Vue.auth.check(['Administrador'])
  }

  if (permissionKey === 'visualizar_relatorios') {
    return Vue.auth.check(['Administrador'])
  }

  return false
}

const hasGranularAccess = (category, action = 'view') => {
  const user = getCurrentUser()
  if (!user) return false

  if (shouldUseCustomPermissions(user)) {
    return hasGranularPermissionFromUser(user, category, action)
  }

  if (hasCustomPermissionsPayload(user)) return false

  if (Vue.auth.check(['Administrador', 'Diretoria', 'Patrimônio'])) {
    return true
  }

  if (Vue.auth.check('Operacional')) {
    if (category === 'valor') return false
    if (action === 'view') return true
    return false
  }

  return false
}

const blockReportsRoute = (to, from, next) => {
  if (isMobileViewport()) {
    next({ name: 'ImoveisRoot' })
    return
  }
  next()
}

const normalizeGeneralAccess = (permissions) => ({
  usuarios: hasGeneralAccessFromPermissions(permissions, 'administracao_usuarios'),
  niveis_acesso: hasGeneralAccessFromPermissions(permissions, 'administracao_niveis_acesso'),
  relatorios: hasGeneralAccessFromPermissions(permissions, 'visualizar_relatorios')
})

const fetchCurrentUser = () => new Promise((resolve) => {
  if (!Vue.auth || typeof Vue.auth.fetch !== 'function') {
    resolve(false)
    return
  }

  Vue.auth.fetch({
    success: () => resolve(true),
    error: () => resolve(false)
  })
})

let sessionSyncPromise = null

const isPublicRoute = (to) => to.matched.some(route => route.meta && route.meta.public === true)

const syncCurrentUserOnNavigation = (to) => {
  if (!Vue.auth || isPublicRoute(to)) {
    return Promise.resolve(false)
  }

  const hasToken = typeof Vue.auth.token === 'function' && !!Vue.auth.token()
  if (!hasToken) {
    return Promise.resolve(false)
  }

  if (!sessionSyncPromise) {
    sessionSyncPromise = fetchCurrentUser().then((synced) => {
      if (!synced) {
        console.warn('[auth-sync] failed to refresh user payload on navigation', { to: to.fullPath })
      }
      return synced
    }).finally(() => {
      sessionSyncPromise = null
    })
  }

  return sessionSyncPromise
}

const guardLog = (type, payload) => {
  const logMethod = type === 'deny' ? console.warn : console.info
  logMethod(`[auth-guard] ${type}`, payload)
}

const ROUTE_GRANULAR_REQUIREMENTS = {
  ImoveisRoot: { category: 'info', action: 'view' },
  ImoveisLista: { category: 'info', action: 'view' },
  ImoveisMapa: { category: 'info', action: 'view' },
  ImoveisAdicionar: { category: 'info', action: 'add' },
  ImoveisDetalhes: { category: 'info', action: 'view' },
  ImoveisDetalhesResumo: { category: 'info', action: 'view' },
  ImoveisDetalhesInfo: { category: 'info', action: 'view' },
  ImoveisDetalhesObservacoes: { category: 'observacoes', action: 'view' },
  ImoveisDetalhesAnexos: { category: 'anexos', action: 'view' },
  ImoveisDetalhesFotos: { category: 'fotos', action: 'view' },
  ImoveisDetalhesEndereco: { category: 'endereco', action: 'view' },
  ImoveisDetalhesRegistro: { category: 'registro', action: 'view' },
  ImoveisDetalhesArea: { category: 'area', action: 'view' },
  ImoveisDetalhesTaxas: { category: 'taxas', action: 'view' },
  ImoveisDetalhesValor: { category: 'valor', action: 'view' },
  ImoveisDetalhesStatus: { category: 'status', action: 'view' },
  ImoveisDetalhesPagamentos: { category: 'pagamentos', action: 'view' },
  VistoriaAdicionar: { category: 'vistoria', action: 'add' },
  VistoriaEditar: { category: 'vistoria', action: 'edit' },
  TarefasLista: { category: 'tarefas', action: 'view' },
  TarefasAdicionar: { category: 'tarefas', action: 'add' },
  TarefasEditar: { category: 'tarefas', action: 'edit' }
}

const resolveGranularRequirement = (to) => {
  if (!to) return null
  if (to.name && ROUTE_GRANULAR_REQUIREMENTS[to.name]) return ROUTE_GRANULAR_REQUIREMENTS[to.name]

  if (typeof to.path === 'string' && to.path.startsWith('/imoveis')) {
    return { category: 'info', action: 'view' }
  }

  return null
}

const resolveGeneralAccessRequirement = (to) => {
  const generalAccessRoute = to.matched.find(route => route.meta && route.meta.generalAccess)
  if (generalAccessRoute) return generalAccessRoute.meta.generalAccess

  if (to.path === '/usuarios') return 'administracao_usuarios'
  if (to.path.startsWith('/usuarios/') && to.path !== '/usuarios/configuracoes') return 'administracao_usuarios'
  if (to.path === '/roles' || to.path.startsWith('/roles/')) return 'administracao_niveis_acesso'
  if (to.path === '/relatorios' || to.path.startsWith('/relatorios/')) return 'visualizar_relatorios'

  return null
}

const shouldRedirectToHomeOnDenied = (to) => {
  if (!to || typeof to.path !== 'string') return false
  if (to.path === '/usuarios') return true
  if (to.path.startsWith('/usuarios/') && to.path !== '/usuarios/configuracoes') return true
  if (to.path === '/roles' || to.path.startsWith('/roles/')) return true
  if (to.path === '/relatorios' || to.path.startsWith('/relatorios/')) return true
  return false
}

const redirectDenied = (to, next) => {
  if (shouldRedirectToHomeOnDenied(to)) {
    next({ path: '/' })
    return
  }
  next({ name: 'Forbidden' })
}

Vue.router = new VueRouter({
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
  mode: 'hash',
  routes: [
    {
      path: '/imoveis/aprovar_pagamento/:token',
      name: 'PublicAprovarPagamento',
      component: PublicPagamentoAvaliacao,
      meta: {
        public: true,
        paymentAction: 'aprovar'
      }
    },
    {
      path: '/imoveis/rejeitar_pagamento/:token',
      name: 'PublicRejeitarPagamento',
      component: PublicPagamentoAvaliacao,
      meta: {
        public: true,
        paymentAction: 'rejeitar'
      }
    },
    {
      path: '/403',
      name: 'Forbidden',
      component: Forbidden
    },
    {
      path: '/404',
      redirect: '/'
    },
    {
      path: '/login',
      name: 'Login',
      component: Login,
      meta: {
        auth: false
      }
    },
    {
      path: '/',
      component: App,
      meta: {
        auth: true
      },
      children: [
        {
          path: '/logout',
          name: 'Logout',
          component: Logout
        },
        {
          path: '/usuarios',
          name: 'Usuarios',
          component: Usuarios,
          children: [
            {
              path: '/usuarios',
              name: 'UsuariosLista',
              component: UsuariosLista,
              meta: {
                generalAccess: 'administracao_usuarios'
              }
            },
            {
              path: '/usuarios/adicionar',
              name: 'UsuariosAdicionar',
              component: UsuariosAdicionar,
              meta: {
                generalAccess: 'administracao_usuarios'
              }
            },
            {
              path: '/usuarios/editar/:id',
              name: 'UsuariosEditar',
              component: UsuariosEditar,
              meta: {
                generalAccess: 'administracao_usuarios'
              }
            },
            {
              path: '/usuarios/configuracoes',
              name: 'UsuariosConfiguracoes',
              component: UsuariosConfiguracoes
            }
          ]
        },
        {
          path: '/roles',
          name: 'RolesLista',
          component: RolesLista,
          meta: {
            generalAccess: 'administracao_niveis_acesso'
          }
        },
        {
          path: '/roles/adicionar',
          name: 'RolesAdicionar',
          component: RolesAdicionar,
          meta: {
            generalAccess: 'administracao_niveis_acesso'
          }
        },
        {
          path: '/roles/editar/:id',
          name: 'RolesEditar',
          component: RolesEditar,
          meta: {
            generalAccess: 'administracao_niveis_acesso'
          }
        },
        {
          path: '/',
          redirect: '/imoveis'
        },
        {
          path: '/imoveis',
          name: 'ImoveisRoot',
          component: ImoveisRoot
        },
        {
          path: '/imoveis/lista',
          name: 'ImoveisLista',
          component: ImoveisLista
        },
        {
          path: '/imoveis/mapa',
          name: 'ImoveisMapa',
          component: ImoveisMapa,
          meta: {
            touchless: true
          }
        },
        {
          path: '/imoveis/adicionar',
          name: 'ImoveisAdicionar',
          component: ImoveisAdicionar
        },
        {
          path: '/imoveis/detalhes/:id',
          name: 'ImoveisDetalhes',
          component: ImoveisDetalhes,
          children: [
            {
              path: '/imoveis/detalhes/:id',
              name: 'ImoveisDetalhesResumo',
              component: ImoveisDetalhesResumo
            },
            {
              path: '/imoveis/info/:id',
              name: 'ImoveisDetalhesInfo',
              component: ImoveisDetalhesInfo
            },
            {
              path: '/imoveis/observacoes/:tipo/:id',
              name: 'ImoveisDetalhesObservacoes',
              component: ImoveisDetalhesObservacoes
            },
            {
              path: '/imoveis/anexos/:tipo/:id',
              name: 'ImoveisDetalhesAnexos',
              component: ImoveisDetalhesAnexos
            },
            {
              path: '/imoveis/fotos/:tipo/:id',
              name: 'ImoveisDetalhesFotos',
              component: ImoveisDetalhesFotos
            },
            {
              path: '/imoveis/endereco/:id',
              name: 'ImoveisDetalhesEndereco',
              component: ImoveisDetalhesEndereco,
              meta: {
                tipo: 'endereco'
              }
            },
            {
              path: '/imoveis/registro/:id',
              name: 'ImoveisDetalhesRegistro',
              component: ImoveisDetalhesRegistro,
              meta: {
                tipo: 'registro'
              }
            },
            {
              path: '/imoveis/area/:id',
              name: 'ImoveisDetalhesArea',
              component: ImoveisDetalhesArea,
              meta: {
                tipo: 'area'
              }
            },
            {
              path: '/imoveis/taxas/:id',
              name: 'ImoveisDetalhesTaxas',
              component: ImoveisDetalhesTaxas,
              meta: {
                tipo: 'taxas'
              }
            },
            {
              path: '/imoveis/valor/:id',
              name: 'ImoveisDetalhesValor',
              component: ImoveisDetalhesValor,
              meta: {
                tipo: 'valor'
              }
            },
            {
              path: '/imoveis/status/:id',
              name: 'ImoveisDetalhesStatus',
              component: ImoveisDetalhesStatus,
              meta: {
                tipo: 'status'
              }
            },
            {
              path: '/imoveis/pagamentos/:id',
              name: 'ImoveisDetalhesPagamentos',
              component: ImoveisDetalhesPagamentos,
              meta: {
                tipo: 'pagamentos'
              }
            },
            {
              path: '/vistoria/adicionar/:id',
              name: 'VistoriaAdicionar',
              component: VistoriaAdicionar,
              meta: {
                auth: ['Administrador', 'Patrimônio']
              }
            },
            {
              path: '/vistoria/editar/:id',
              name: 'VistoriaEditar',
              component: VistoriaEditar,
              meta: {
                auth: ['Administrador', 'Patrimônio']
              }
            },
            {
              path: '/tarefas/:id',
              name: 'TarefasLista',
              component: TarefasLista,
              meta: {
                auth: ['Administrador', 'Patrimônio']
              }
            },
            {
              path: '/tarefas/adicionar/:id',
              name: 'TarefasAdicionar',
              component: TarefasAdicionar,
              meta: {
                auth: ['Administrador', 'Patrimônio']
              }
            },
            {
              path: '/tarefas/editar/:id/:tarefa',
              name: 'TarefasEditar',
              component: TarefasEditar,
              meta: {
                auth: ['Administrador', 'Patrimônio']
              }
            }
          ]
        },
        {
          path: '/relatorios',
          name: 'RelatoriosRoot',
          component: RelatoriosRoot,
          beforeEnter: blockReportsRoute,
          meta: {
            generalAccess: 'visualizar_relatorios'
          },
          children: [
            {
              path: '/relatorios/historico/data',
              name: 'RelatoriosHistoricoData',
              component: RelatoriosHistorico
            },
            {
              path: '/relatorios/historico/usuario',
              name: 'RelatoriosHistoricoUsuario',
              component: RelatoriosHistorico
            },
            {
              path: '/relatorios/comparativos',
              name: 'RelatoriosComparativos',
              component: RelatoriosComparativos
            },
            {
              path: '/relatorios/pagamentos',
              name: 'RelatoriosPagamentos',
              component: RelatoriosPagamentos
            }
          ]
        }
      ]
    }
  ]
})

Vue.router.beforeEach(async (to, from, next) => {
  const publicRoute = isPublicRoute(to)
  const granularRequirement = publicRoute ? null : resolveGranularRequirement(to)
  const requiredPermission = publicRoute ? null : resolveGeneralAccessRequirement(to)
  const isProtectedRoute = !!granularRequirement || !!requiredPermission

  const didSyncUser = await syncCurrentUserOnNavigation(to)

  if (!Vue.auth) {
    next()
    return
  }

  const hasToken = typeof Vue.auth.token === 'function' && !!Vue.auth.token()
  if (!hasToken) {
    next()
    return
  }

  let user = getCurrentUser()

  if (!Vue.auth.check()) {
    next()
    return
  }

  const invalidUserPayload = !user || !user.id || isCustomPermissionsPayloadInvalid(user)
  if (isProtectedRoute && (!didSyncUser || invalidUserPayload)) {
    guardLog('deny', {
      to: to.fullPath,
      userId: user && user.id,
      reason: !didSyncUser ? 'user_payload_refresh_failed' : 'invalid_custom_permissions_payload'
    })
    redirectDenied(to, next)
    return
  }

  if (granularRequirement) {
    const category = granularRequirement.category
    const action = granularRequirement.action || 'view'
    const canAccess = hasGranularAccess(category, action)

    if (!canAccess) {
      guardLog('deny', {
        to: to.fullPath,
        userId: user && user.id,
        reason: `missing_granular_permission:${category}.${action}`
      })
      redirectDenied(to, next)
      return
    }
  }

  if (!requiredPermission) {
    next()
    return
  }

  const rawPermissions = user ? user.custom_permissions : null
  const normalizedPermissions = normalizePermissions(rawPermissions)
  const normalizedGeneralAccess = normalizeGeneralAccess(normalizedPermissions)
  const hasAccess = hasGeneralAccess(requiredPermission)

  if (hasAccess) {
    guardLog('allow', {
      to: to.fullPath,
      userId: user && user.id,
      requiredPermission,
      normalizedGeneralAccess
    })
    next()
    return
  }

  guardLog('deny', {
    to: to.fullPath,
    userId: user && user.id,
    rawPermissions,
    normalizedPermissions,
    normalizedGeneralAccess,
    reason: `missing_required_permission:${requiredPermission}`
  })

  redirectDenied(to, next)
})

export default Vue.router



// WEBPACK FOOTER //
// ./src/router/index.js